import { useFragment } from "react-relay"
import { FileUploadRequestFragment$key } from "@/modules/patients/services/__generated__/FileUploadRequestFragment.graphql"
import { FileUploadRequestFragment } from "@/modules/patients/services/FileUploadRequestFragment"
import { useCancelFileUploadRequestMutation } from "@/modules/patients/services/useCancelFileUploadRequestMutation"
import { useFileUploadRequestMutation } from "@/modules/patients/services/useFileUploadRequestMutation"
import { FileUploadRequestType } from "@/types/__generated__/union-types"

const DOCUMENT_REQUIRED: FileUploadRequestType = "DOCUMENT_REQUIRED"

type GetRequestFileUploadProps = {
  appointmentId: string
  fileUploadRequestRef?: FileUploadRequestFragment$key | null
}

const useRequestFileUpload = ({ appointmentId, fileUploadRequestRef }: GetRequestFileUploadProps) => {
  const fileUploadRequestData = useFragment<FileUploadRequestFragment$key>(
    FileUploadRequestFragment,
    fileUploadRequestRef,
  )
  const isUploadPending = !!(fileUploadRequestData && fileUploadRequestData.status === "PENDING")

  const [requestUploadFile, isSendingRequest] = useFileUploadRequestMutation()
  const [cancelRequest, isCancelingRequest] = useCancelFileUploadRequestMutation()

  const handleSendRequest = (documentType?: FileUploadRequestType, onSendRequestCompleted?: () => void) => {
    if (!appointmentId) return

    // For an initial request, the document type always be "documents required"
    // For subsequent requests, the request document type is selected by doctor
    requestUploadFile({
      requestDocumentType: !documentType ? DOCUMENT_REQUIRED : documentType,
      appointmentId,
      onCompleted() {
        onSendRequestCompleted?.()
      },
      updater(store, data) {
        try {
          const appointmentRecordProxy = store.get(appointmentId)
          const fileUploadRequest = data?.createFileUploadRequest.fileUploadRequest

          if (fileUploadRequest?.id) {
            const newRecordProxy = store.get(fileUploadRequest.id)
            if (newRecordProxy) {
              appointmentRecordProxy?.setLinkedRecord(newRecordProxy, "fileUploadRequest")
            }
          }
        } catch (error) {
          console.error("Error while processing data:", error)
        }
      },
    })
  }

  const handleCancelRequest = () => {
    if (!appointmentId) return

    cancelRequest({
      variables: {
        input: {
          appointmentId,
        },
      },
    })
  }

  return {
    isUploadPending,
    fileUploadRequestData,
    isSendingRequest,
    handleSendRequest,
    isCancelingRequest,
    handleCancelRequest,
  }
}

export { useRequestFileUpload, DOCUMENT_REQUIRED }
